import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

export const DEFAULT_PAGE_SIZE = 25;

/** Paginarea și filtrele trăiesc în URL: link-uri partajabile, back / forward funcțional. */
export function useUrlState<K extends string>(keys: readonly K[]) {
  const [params, setParams] = useSearchParams();
  const values = useMemo(() => {
    const out = {} as Record<K, string>;
    for (const k of keys) out[k] = params.get(k) ?? '';
    return out;
  }, [params, keys]);
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);
  const pageSize = Math.min(
    100,
    Math.max(1, Number(params.get('pageSize') ?? String(DEFAULT_PAGE_SIZE)) || DEFAULT_PAGE_SIZE),
  );

  const set = useCallback(
    (patch: Partial<Record<K | 'page' | 'pageSize', string | number | null | undefined>>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          let touchedFilter = false;
          for (const [k, v] of Object.entries(patch)) {
            if (k !== 'page' && k !== 'pageSize') touchedFilter = true;
            if (v === null || v === undefined || v === '') next.delete(k);
            else next.set(k, String(v));
          }
          // Schimbarea unui filtru revine la prima pagină.
          if (touchedFilter && !('page' in patch)) next.delete('page');
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  return { values, page, pageSize, set };
}

/** Parametrii de query pentru API din starea URL (fără valorile goale). */
export function queryParams<T extends Record<string, string | number | undefined>>(
  obj: T,
): Partial<T> {
  const out: Partial<T> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined && v !== '') (out as Record<string, unknown>)[k] = v;
  }
  return out;
}
